/**
 * TeamBudgetSection.tsx - Team management + team target sales & incentive settings
 *
 * Phase 2: payroll-management.tsx component decomposition
 * FR-11/FR-12: Extracted JSX section
 */

import React from 'react';
import { usePayrollRender } from './PayrollRenderContext.tsx';
import { resolveTeamIncentiveRatio, calculateTeamSalaryTotal, resolveFinalTargetSales,
  getTeamAdjustmentCarryover } from '../../utils/payrollCalculations.ts';
// ⚠️ 컴포넌트 분해(3125d07) 때부터 빠져 있던 import. 이 두 함수는 '조정 토글 on + 지난달 데이터 있음'
//    일 때만 렌더 경로에서 호출되는데, 그 조건은 원장이 토글을 손으로 켠 세션에서만 참이라 잠복해 있었다.
//    이월이 있는 달을 토글 on으로 시작하게 바꾸자(eab9298) 처음 실행되며 ReferenceError로 화면이 비었다.
import { checkTeamAndLeaderMatch, calculatePreviousMonthUnderperformance } from '../../services/teamTargetSalesService.ts';

export const TeamBudgetSection: React.FC = () => {
  const {
    addTeam,
    applyPreviousMonthAdjustment,
    budgetData,
    calculatePureTargetSales,
    calculateTeamStatistics,
    canEditPayroll,
    employees,
    getEffectivePersonnelPercentage,
    getSortedTeams,
    handleAdjustmentToggle,
    importedFromMonth,
    isLoadingActualSales,
    isLoadingPreviousMonthData,
    localTeams,
    monthlyTeamTargetSales,
    previousMonthData,
    removeTeam,
    selectedMonth,
    setBudgetData,
    setIsLoadingActualSales,
    setTeamSortType,
    shouldMaskTeamData,
    teamActualSales,
    teamIncentiveCalculations,
    teamSortType,
    teamsData,
    underperformanceDeductionRate,
    updatePersonnelPercentage,
    updateTeamIncentiveRatio,
    updateUnderperformanceDeductionRate,
    payrollEditMode,
  } = usePayrollRender();

  return (
    <>
      {/* 팀 관리 섹션 */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm mb-3">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
          <div className="flex flex-col">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {selectedMonth.replace('-', '년 ')}월 팀 관리
              {(() => {
                const currentYearMonth = new Date().toISOString().slice(0, 7);
                const isCurrentMonth = selectedMonth === currentYearMonth;
                const monthlyData = budgetData.monthlyData?.[selectedMonth];

                // 🔧 더 포괄적인 월별 데이터 존재 확인 (teams, employees, 또는 importedFromMonth 중 하나라도 있으면)
                const hasMonthlyTeams = monthlyData?.teams && Array.isArray(monthlyData.teams) && monthlyData.teams.length > 0;
                const hasMonthlyEmployees = monthlyData?.employees && Array.isArray(monthlyData.employees) && monthlyData.employees.length > 0;
                const hasImportedData = monthlyData?.importedFromMonth; // 다른 월에서 가져온 데이터

                const hasMonthlyData = hasMonthlyTeams || hasMonthlyEmployees || hasImportedData;

                if (hasMonthlyData) {
                  // 어떤 데이터가 있는지 표시
                  const dataTypes = [];
                  if (hasMonthlyTeams) dataTypes.push('팀');
                  if (hasMonthlyEmployees) dataTypes.push('직원');
                  if (hasImportedData && !hasMonthlyTeams && !hasMonthlyEmployees) dataTypes.push('가져온 데이터');

                  const dataInfo = dataTypes.length > 0 ? ` (${dataTypes.join(', ')})` : '';
                  return <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">📊 월별 데이터{dataInfo}</span>;
                } else if (isCurrentMonth) {
                  return <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">🔥 현재 월 (전역 반영)</span>;
                } else {
                  return <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">🌐 전역 데이터 사용</span>;
                }
              })()}
            </h2>
            <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded-md">
              {(() => {
                const currentYearMonth = new Date().toISOString().slice(0, 7);
                const isCurrentMonth = selectedMonth === currentYearMonth;
                const monthlyData = budgetData.monthlyData?.[selectedMonth];

                // 🔧 개선된 상태 확인 로직 (UI 표시와 동일한 로직 사용)
                const hasMonthlyTeams = monthlyData?.teams && Array.isArray(monthlyData.teams) && monthlyData.teams.length > 0;
                const hasMonthlyEmployees = monthlyData?.employees && Array.isArray(monthlyData.employees) && monthlyData.employees.length > 0;
                const hasImportedData = monthlyData?.importedFromMonth;
                const hasMonthlyData = hasMonthlyTeams || hasMonthlyEmployees || hasImportedData;

                if (isCurrentMonth) {
                  return "현재 월의 팀 변경사항은 전체 시스템에 반영됩니다.";
                } else if (hasMonthlyData) {
                  let message = `${selectedMonth}월 전용 데이터입니다. 다른 월에는 영향을 주지 않습니다.`;
                  if (hasImportedData) {
                    message += ` (${hasImportedData}월에서 가져온 데이터)`;
                  }
                  return message;
                } else {
                  return "월별 데이터가 없어 전역 데이터를 사용합니다. '지난달 내역 불러오기'를 통해 데이터를 가져올 수 있습니다.";
                }
              })()}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">정렬:</span>
              <select
                className="border border-gray-300 rounded-lg p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[120px]"
                value={teamSortType}
                onChange={(e) => setTeamSortType(e.target.value as SortType)}
              >
                <option value="numeric">숫자순</option>
                <option value="alphabetic">알파벳순</option>
                <option value="korean">가나다순</option>
              </select>
            </div>
            {/* 🔒 팀 구성 변경은 전체 편집 권한 전용 */}
            {payrollEditMode === 'full' && (
              <button 
                className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 sm:py-2 sm:px-4 text-sm font-medium rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={addTeam}
              >
                {selectedMonth.replace('-', '년 ')}월 팀 추가
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {getSortedTeams(localTeams, teamSortType).map(teamName => (
            <div key={teamName} className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-sm sm:text-base text-gray-800 truncate mr-2">{teamName}</span>
                {/* 🔒 팀 삭제는 전체 편집 권한 전용.
                    removeTeam은 Firestore teams 문서를 지우고 소속 직원을 '미지정'으로 옮긴 뒤
                    전체 저장(internalSaveAllData)까지 실행한다 — 제한 권한자에게 열어두면
                    클릭 한 번으로 그 달 급여가 화면 상태로 통째로 덮어써진다. */}
                {payrollEditMode === 'full' && (
                  <button 
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded text-sm font-medium transition-colors flex-shrink-0"
                    onClick={() => removeTeam(teamName)}
                    title={`${teamName} 팀 삭제`}
                  >
                    삭제
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 팀별 목표매출 및 인센티브 설정 */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-3">
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              팀별 목표매출 & 인센티브
          </h2>
            <button
              onClick={async () => {
                try {
                  setIsLoadingActualSales(true);
                  const { TeamPerformanceService } = await import('../../services/teamPerformanceService.ts');
                  const result = await TeamPerformanceService.calculateTeamPerformance(selectedMonth, teamsData, employees, budgetData);
                  // 🛡️ result.actualSalesResult를 함께 전달 — 오귀속 의심 게이트(단일 초크포인트)가
                  //    진료의사 매핑 부재 창의 '미지정' 귀속 결과 DB 영속을 이 경로에서도 차단한다.
                  const saveResult = await TeamPerformanceService.saveTeamPerformanceData(selectedMonth, result.teamPerformanceData, result.totalActualSales, result.teamTargetSales, budgetData, setBudgetData, result.actualSalesResult);
                  // 🛡️ 게이트가 저장을 건너뛴 경우 '완료'로 오인하지 않게 분기 안내
                  if (saveResult?.skipped) {
                    alert('진료의사 매핑 부재 상태가 감지되어 팀 성과 저장을 보류했습니다.\n화면 표시도 갱신되지 않았습니다. 매핑 복구 후 다시 새로고침해 주세요.');
                  } else {
                    alert('성과 데이터 새로고침 완료');
                  }
                } catch (error) {
                  alert('새로고침 실패');
                } finally {
                  setIsLoadingActualSales(false);
                }
              }}
              disabled={isLoadingActualSales}
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              {isLoadingActualSales ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                  <span>새로고침중</span>
                </>
              ) : (
                <span>새로고침</span>
              )}
            </button>
          </div>
        </div>
        
        {/* 초컴팩트 설정 패널 */}
        <div className="p-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            
            {/* 인건비율 설정 */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
              canEditPayroll 
                ? 'bg-blue-50 border-blue-200' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <span className={`font-medium ${
                canEditPayroll ? 'text-blue-700' : 'text-gray-600'
              }`}>인건비율:</span>
              <input 
                type="number" 
                value={(() => {
                  const monthlyPersonnelPercentage = budgetData.monthlyData?.[selectedMonth]?.personnelPercentage;
                  return monthlyPersonnelPercentage !== undefined ? monthlyPersonnelPercentage.toFixed(1) : '0.0';
                })()} 
                onChange={(e) => updatePersonnelPercentage(parseFloat(e.target.value) || 0)}
                step="0.1" min="0" max="100"
                disabled={!canEditPayroll}
                className={`w-16 px-2 py-1 border rounded text-center font-bold focus:ring-1 ${
                  canEditPayroll 
                    ? 'bg-white border-blue-300 text-blue-700 focus:ring-blue-500' 
                    : 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              />
              <span className={canEditPayroll ? 'text-blue-600' : 'text-gray-500'}>%</span>
              <button 
                className={`px-2 py-1 rounded text-xs transition-colors ${
                  canEditPayroll 
                    ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!canEditPayroll}
                onClick={() => {
                  const currentPercentage = budgetData.monthlyData?.[selectedMonth]?.personnelPercentage || 0;
                  updatePersonnelPercentage(currentPercentage);
                }}
              >
                전체적용
              </button>
              {!canEditPayroll && (
                <span className="text-xs text-gray-500 ml-2">🔒 읽기 전용</span>
              )}
            </div>
          
            {/* 차감비율 설정 */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
              canEditPayroll 
                ? 'bg-orange-50 border-orange-200' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <span className={`font-medium ${
                canEditPayroll ? 'text-orange-700' : 'text-gray-600'
              }`}>차감율:</span>
              <input 
                type="number" 
                value={underperformanceDeductionRate}
                onChange={(e) => updateUnderperformanceDeductionRate(parseFloat(e.target.value) || 0)}
                step="1" min="0" max="100"
                disabled={!canEditPayroll}
                className={`w-16 px-2 py-1 border rounded text-center font-bold focus:ring-1 ${
                  canEditPayroll 
                    ? 'bg-white border-orange-300 text-orange-700 focus:ring-orange-500' 
                    : 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              />
              <span className={canEditPayroll ? 'text-orange-600' : 'text-gray-500'}>%</span>
              {!canEditPayroll && (
                <span className="text-xs text-gray-500 ml-2">🔒 읽기 전용</span>
              )}
            </div>
        
            {/* 지난달 조정 설정 */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
              canEditPayroll 
                ? 'bg-emerald-50 border-emerald-200' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <span className={`font-medium ${
                canEditPayroll ? 'text-emerald-700' : 'text-gray-600'
              }`}>지난달 미달성 조정:</span>
              {canEditPayroll ? (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={applyPreviousMonthAdjustment}
                    onChange={(e) => handleAdjustmentToggle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-1 rounded-full peer peer-checked:bg-emerald-500 transition-all"></div>
                  <div className="absolute left-0.5 top-0.5 bg-white w-3 h-3 rounded-full transition-all peer-checked:translate-x-4"></div>
                </label>
              ) : (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  applyPreviousMonthAdjustment 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {applyPreviousMonthAdjustment ? '활성화' : '비활성화'}
                </span>
              )}
              {!canEditPayroll && (
                <span className="text-xs text-gray-500 ml-2">🔒 읽기 전용</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-2 px-2 text-center font-bold text-gray-700 text-xs">팀명</th>
                <th className="py-2 px-1 text-center font-bold text-gray-700 text-xs">팀장</th>
                <th className="py-2 px-1 text-center font-bold text-gray-700 text-xs">팀원수</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">인건비합계</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">인건비율(%)</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">순수목표매출</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">최종목표매출</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">실제매출</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">초과/미달</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">차감분</th>
                <th className="py-2 px-1 text-right font-bold text-gray-700 text-xs">인센티브산출매출</th>
                <th className="py-2 px-1 text-center font-bold text-gray-700 text-xs">인센티브비율(%)</th>
                <th className="py-2 px-1 text-center font-bold text-gray-700 text-xs">팀장/팀원배분</th>
                <th className="py-2 px-1 text-center font-bold text-gray-700 text-xs">지난달조정</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-xs">
              {(() => {
                const sortedTeams = getSortedTeams(localTeams, teamSortType);
                // 렌더링 로그를 한 번만 출력하도록 수정
                if (sortedTeams.length > 0) {
                }
                
                return sortedTeams.map(teamName => { 
      // 🔧 로컬 상태 employees 사용으로 실시간 인건비 반영
      const teamEmployees = employees.filter(emp => emp.team === teamName);
      const teamLeader = teamEmployees.find(emp => emp.position === '팀장');
                  const teamMemberCount = teamEmployees.filter(emp => emp.position === '팀원').length;
                  
                  // 🔧 디버깅 로그 추가: 팀별 인건비 총액 계산 과정 상세 로그
                  const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName, employees);

                  // 디버깅 로그는 별도로 처리
                  teamEmployees.forEach(emp => {
                    const empTotal = (emp.salary || 0) + (emp.retirement || 0) + (emp.meal || 0) + (emp.housing || 0) + 
                      (emp.overtime || 0) + (emp.individual || 0) + (emp.other || 0);
                  });

                  // 월별 데이터를 우선적으로 사용하여 목표매출 가져오기 (월별 데이터가 없으면 0으로 시작)
                  const monthlyTeamTargetSales = budgetData.monthlyData?.[selectedMonth]?.teamTargetSales;
                  const teamTarget = monthlyTeamTargetSales && typeof monthlyTeamTargetSales[teamName] !== 'undefined'
                                     ? monthlyTeamTargetSales[teamName]
                                     : 0;
                  
                  // 순수 목표매출 가져오기
                  const teamPureTargetSales = budgetData.monthlyData?.[selectedMonth]?.teamPureTargetSales || budgetData.teamPureTargetSales || {};
                  
                  // 월별 데이터를 우선적으로 사용하여 인건비율 계산 (월별 데이터가 없으면 전체 인건비율 사용)
                  const monthlyTeamPersonnelPercentages = budgetData.monthlyData?.[selectedMonth]?.teamPersonnelPercentages;
                  const monthlyPersonnelPercentage = budgetData.monthlyData?.[selectedMonth]?.personnelPercentage;
                  const teamPersonnelPercentage = monthlyTeamPersonnelPercentages && typeof monthlyTeamPersonnelPercentages[teamName] !== 'undefined'
                                                  ? monthlyTeamPersonnelPercentages[teamName]
                                                  : (monthlyPersonnelPercentage || budgetData.personnelPercentage || 19);
                  
                  // 🔧 팀 성과 데이터 우선 사용 (TeamPerformanceService에서 저장한 데이터)
                  const savedTeamPerformanceData = budgetData.monthlyData?.[selectedMonth]?.teamPerformanceData?.[teamName];
                  
                  // [비활성화] 팀별 실제매출 확인 로그
                  
                  // 🚀 수정: 실제매출도 직접 계산 (저장된 데이터 사용 안함)
                  const teamActualSalesAmount = teamActualSales[teamName] || 0;
                  
                  // 🔧 수정: 실시간 계산된 최종목표매출을 기준으로 초과/미달 계산 (기존 변수 재사용)
                  // 지난달 미달성 조정 계산
                  const currentLeaderName = teamLeader?.name || '';
                  const canApplyAdjustment = applyPreviousMonthAdjustment && 
                                           previousMonthData && 
                                           checkTeamAndLeaderMatch(teamName, currentLeaderName, previousMonthData);
                  const previousUnderperformance = canApplyAdjustment ? calculatePreviousMonthUnderperformance(teamName, previousMonthData) : 0;
                  
                  const realTimePureTarget = calculatePureTargetSales(teamSalaryTotal, getEffectivePersonnelPercentage(teamName));
                  // 정본: 순수 + 저장된 이월금액 (급여 인센티브 기준과 동일)
                  const realTimeFinalTarget = resolveFinalTargetSales(budgetData, selectedMonth, teamName, realTimePureTarget);
                  
                  const exceededSales = Math.max(0, teamActualSalesAmount - realTimeFinalTarget);
                  const underperformanceSales = exceededSales > 0 ? 0 : Math.max(0, realTimeFinalTarget - teamActualSalesAmount);
                  
                  // 🚀 수정: 실시간 계산된 차감분 사용 (월급 변경 시 즉시 반영)
                  // 차감분 실시간 계산 로직 (전체 팀의 초과/미달 상황 고려)
                  const allTeamsData = getSortedTeams(localTeams, teamSortType).map(tn => {
                    const teamEmps = employees.filter(emp => emp.team === tn);
                    const teamSalary = calculateTeamSalaryTotal(employees, tn, employees);
                    const teamPure = calculatePureTargetSales(teamSalary, getEffectivePersonnelPercentage(tn));
                    const teamLdr = teamEmps.find(emp => emp.role === '팀장');
                    const canAdjust = applyPreviousMonthAdjustment && 
                                     previousMonthData && 
                                     checkTeamAndLeaderMatch(tn, teamLdr?.name || '', previousMonthData);
                    const prevUnder = canAdjust ? calculatePreviousMonthUnderperformance(tn, previousMonthData) : 0;
                    const finalTarget = resolveFinalTargetSales(budgetData, selectedMonth, tn, teamPure);
                    const actual = teamActualSales[tn] || 0;
                    const exceeded = Math.max(0, actual - finalTarget);
                    const underperformed = Math.max(0, finalTarget - actual);
                    return { teamName: tn, exceeded, underperformed };
                  });
                  
                  const totalExceeded = allTeamsData.reduce((sum, t) => sum + t.exceeded, 0);
                  const totalUnderperformed = allTeamsData.reduce((sum, t) => sum + t.underperformed, 0);
                  const totalDeduction = totalUnderperformed * (underperformanceDeductionRate / 100);
                  
                  let realTimeDeductionAmount = 0;
                  if (exceededSales > 0 && totalExceeded > 0 && totalDeduction > 0) {
                    const deductionRatio = exceededSales / totalExceeded;
                    realTimeDeductionAmount = Math.round(totalDeduction * deductionRatio);
                  }
                  
                  const deductionAmount = realTimeDeductionAmount;
                  
                  // 🚀 수정: 실시간 계산된 인센티브 산출 매출 사용 (월급 변경 시 즉시 반영)
                  const realTimeIncentiveCalculationSales = Math.max(0, exceededSales - deductionAmount);
                  const incentiveCalculationSales = realTimeIncentiveCalculationSales;
                  
                  // 🔧 연동 상태 확인 로그 추가
                  
                  // 팀별 인센티브 비율 (월별 우선 → 전역 동결 폴백)
                  const teamIncentiveSettings = resolveTeamIncentiveRatio(budgetData, selectedMonth, teamName);
                  
                  return (
                    <tr key={teamName} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-1 px-1 text-center">{teamName}</td>
                      <td className="py-1 px-1 text-center w-16 sm:w-18 min-w-16">{teamLeader ? teamLeader.name : '없음'}</td>
                      <td className="py-1 px-1 text-center">{teamMemberCount}명</td>
                      <td className="py-1 px-1 text-right tabular-nums text-gray-900 w-14 sm:w-16 min-w-14">
                        {shouldMaskTeamData(teamName) ? (
                          <span className="font-semibold text-gray-500">****</span>
                        ) : (
                          teamSalaryTotal.toLocaleString()
                        )}
                      </td>
                      <td className="py-1 px-1 text-right tabular-nums">
                        <span className="text-sm font-medium text-gray-700">
                          {shouldMaskTeamData(teamName) ? (
                            <span className="font-semibold text-gray-500">****</span>
                          ) : (
                            (() => {
                              const teamPersonnelPercentage = getEffectivePersonnelPercentage(teamName);
                              return teamPersonnelPercentage > 0 ? `${teamPersonnelPercentage.toFixed(1)}%` : '-';
                            })()
                          )}
                        </span>
                      </td>
                      {/* 순수 목표매출 표시 (읽기 전용) */}
                      <td className="py-1 px-1 text-right tabular-nums">
                        <div className="flex flex-col items-end space-y-1">
                          {shouldMaskTeamData(teamName) ? (
                            <span className="font-semibold text-gray-500">****</span>
                          ) : (
                            <span className="text-sm font-medium text-gray-900">
                              {calculatePureTargetSales(teamSalaryTotal, getEffectivePersonnelPercentage(teamName)).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* 최종 목표매출 입력 필드 */}
                      <td className="py-1 px-1 text-right tabular-nums">
                        {(() => {
                          
                          // 🔧 지난달 미달성 조정 기능 관련 계산
      const currentLeaderName = teamLeader?.name || '';
                          const canApplyAdjustment = applyPreviousMonthAdjustment && 
                                                   previousMonthData && 
                                                   checkTeamAndLeaderMatch(teamName, currentLeaderName, previousMonthData);
      const previousUnderperformance = canApplyAdjustment ? calculatePreviousMonthUnderperformance(teamName, previousMonthData) : 0;
      // 저장된 이월금액 — 배지는 '적용될 수 있는 값'이 아니라 '실제로 들어가 있는 값'을 보여준다
      const appliedCarryover = getTeamAdjustmentCarryover(budgetData?.monthlyData?.[selectedMonth], teamName);
      
                          return (
                            <div className="flex flex-col items-end space-y-1">
                              {shouldMaskTeamData(teamName) ? (
                                <span className="font-semibold text-gray-500">****</span>
                              ) : (
                                <span className="text-sm font-medium text-gray-900">
                                  {(() => {
                                    // 🔧 실시간 계산된 순수목표매출 기반으로 최종목표매출 계산
                                    const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName, employees);
                                    const realTimePureTarget = calculatePureTargetSales(teamSalaryTotal, getEffectivePersonnelPercentage(teamName));

                                    // 정본: 순수 + 저장된 이월금액
                                    const realTimeFinalTarget = resolveFinalTargetSales(budgetData, selectedMonth, teamName, realTimePureTarget);

                                    return realTimeFinalTarget.toLocaleString();
                                  })()}
                                </span>
                              )}
                              
                              {/* 지난달 미달성 조정 정보 표시 */}
                              {applyPreviousMonthAdjustment && !shouldMaskTeamData(teamName) && (
                                <div className="w-full">
                                  {isLoadingPreviousMonthData ? (
                                    <div className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1">
                                      🔄 로딩 중...
                                    </div>
                                  ) : !previousMonthData ? (
                                    <div className="text-xs text-red-500 bg-red-50 rounded px-2 py-1">
                                      ❌ 지난달 데이터 없음
                                    </div>
                                  ) : appliedCarryover > 0 ? (
                                    // 실제로 목표에 들어가 있는 이월금액 — 저장된 값 그대로
                                    <div className="text-xs bg-orange-50 border border-orange-200 rounded px-2 py-1">
                                      <div className="text-orange-700 font-semibold">
                                        ✅ 이월 적용됨
                                      </div>
                                      <div className="text-orange-600">
                                        이월: {appliedCarryover.toLocaleString()}원
                                      </div>
                                    </div>
                                  ) : canApplyAdjustment ? (
                                    previousUnderperformance > 0 ? (
                                      <div className="text-xs bg-orange-50 border border-orange-200 rounded px-2 py-1">
                                        <div className="text-orange-700 font-semibold">
                                          ⏳ 조정 대상 (미적용)
                                        </div>
                                        <div className="text-orange-600">
                                          미달: {previousUnderperformance.toLocaleString()}원
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-xs text-green-500 bg-green-50 rounded px-2 py-1">
                                        ✅ 초과달성 (조정 불필요)
                                      </div>
                                    )
                                  ) : (
                                    <div className="text-xs text-red-500 bg-red-50 rounded px-2 py-1">
                                      {!currentLeaderName ? (
                                        '❌ 팀장 없음'
                                      ) : (
                                        '❌ 팀장 불일치'
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-1 px-1 text-right tabular-nums">
                        <div className="flex flex-col items-end">
                          {shouldMaskTeamData(teamName) ? (
                            <span className="font-semibold text-gray-500">****</span>
                          ) : (
                            <>
                              <span className={`font-semibold ${teamActualSalesAmount >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                                {isLoadingActualSales ? '로딩...' : teamActualSalesAmount.toLocaleString()}
                                {(() => {
                                  return null;
                                })()}
                              </span>
                              {!isLoadingActualSales && realTimeFinalTarget > 0 && (
                                <span className={`text-xs ${teamActualSalesAmount >= realTimeFinalTarget ? 'text-green-600' : 'text-orange-600'}`}>
                                  ({((teamActualSalesAmount / realTimeFinalTarget) * 100).toFixed(1)}%)
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-1 px-1 text-right tabular-nums">
                        <div className="flex flex-col items-end">
                          {shouldMaskTeamData(teamName) ? (
                            <span className="font-semibold text-gray-500">****</span>
                          ) : (() => {
                            const difference = exceededSales > 0 ? exceededSales : -underperformanceSales;
                            return (
                              <>
                                <span className={`font-semibold ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                  {isLoadingActualSales ? '로딩...' : difference.toLocaleString()}
                                </span>
                                {!isLoadingActualSales && (
                                  <span className={`text-xs ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                    {difference > 0 ? '초과달성' : difference < 0 ? '미달성' : '목표달성'}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      {/* 🔧 차감분 컬럼 */}
                      <td className="py-1 px-1 text-right tabular-nums">
                        <div className="flex flex-col items-end">
                          {shouldMaskTeamData(teamName) ? (
                            <span className="font-semibold text-gray-500">****</span>
                          ) : (
                            <>
                              <span className={`font-semibold ${deductionAmount > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {deductionAmount.toLocaleString()}
                              </span>
                              {deductionAmount > 0 && (
                                <span className="text-xs text-red-600">
                                  차감
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      {/* 🔧 인센티브 산출 매출 컬럼 */}
                      <td className="py-1 px-1 text-right tabular-nums">
                        <div className="flex flex-col items-end">
                          {shouldMaskTeamData(teamName) ? (
                            <span className="font-semibold text-gray-500">****</span>
                          ) : (
                            <>
                              <span className={`font-semibold ${incentiveCalculationSales > 0 ? 'text-purple-600' : 'text-gray-600'}`}>
                                {incentiveCalculationSales.toLocaleString()}
                              </span>
                              {incentiveCalculationSales > 0 && (
                                <span className="text-xs text-purple-600">
                                  인센티브 대상
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-1 px-1 text-center w-20 sm:w-24 min-w-20">
                        {shouldMaskTeamData(teamName) ? (
                          <span className="text-gray-500">****</span>
                        ) : (
                          <>
                            <input
                              type="number"
                              value={teamIncentiveSettings.performance}
                              onChange={(e) => updateTeamIncentiveRatio(teamName, 'performance', e.target.value)}
                              step="0.1" min="0" max="100"
                              disabled={!canEditPayroll}
                              className={`border rounded-lg p-1 w-16 sm:w-14 text-center text-sm focus:ring-2 transition-all ${
                                canEditPayroll 
                                  ? 'border-gray-300 focus:ring-green-500 focus:border-transparent' 
                                  : 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed'
                              }`}
                              placeholder="0"
                            />
                            <span className={`text-xs ml-1 ${canEditPayroll ? 'text-gray-500' : 'text-gray-400'}`}>%</span>
                          </>
                        )}
                      </td>
                      <td className="py-1 px-1 text-center w-40 min-w-40">
                        {shouldMaskTeamData(teamName) ? (
                          <span className="text-gray-500">****</span>
                        ) : (
                          <div className="flex items-center justify-center space-x-1">
                            <input
                              type="number"
                              value={teamIncentiveSettings.team}
                              onChange={(e) => updateTeamIncentiveRatio(teamName, 'team', e.target.value)}
                              min="0"
                              max="100"
                              step="0.1"
                              disabled={payrollEditMode !== 'full'}
                              className={`border border-gray-300 rounded-lg p-1 w-14 text-center text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all${payrollEditMode !== 'full' ? ' bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                              title={payrollEditMode !== 'full'
                                ? '인센티브 배분은 관리자만 변경할 수 있습니다'
                                : `${teamName} 팀장 비율`}
                              placeholder="0"
                            />
                            <span className="text-xs text-gray-600">%</span>
                            <span className="text-xs text-gray-400 mx-1">/</span>
                            <input
                              type="number"
                              value={teamIncentiveSettings.member}
                              onChange={(e) => updateTeamIncentiveRatio(teamName, 'member', e.target.value)}
                              min="0"
                              max="100"
                              step="0.1"
                              disabled={payrollEditMode !== 'full'}
                              className={`border border-gray-300 rounded-lg p-1 w-14 text-center text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all${payrollEditMode !== 'full' ? ' bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                              title={payrollEditMode !== 'full'
                                ? '인센티브 배분은 관리자만 변경할 수 있습니다'
                                : `${teamName} 팀원 비율`}
                              placeholder="0"
                            />
                            <span className="text-xs text-gray-600">%</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                });
              })()}
              {/* 팀별 목표매출 테이블 합계 행 */}
              <tr className="bg-blue-50 font-semibold text-blue-800 border-t border-blue-200">
                <td className="py-1 px-1 text-center font-bold">합계</td>
                <td className="py-1 px-1 text-center w-16 sm:w-18 min-w-16">-</td>
                <td className="py-1 px-1 text-center">
                  {calculateTeamStatistics(getSortedTeams(localTeams, teamSortType), employees, teamActualSales, teamIncentiveCalculations, budgetData, selectedMonth).totalEmployees}명
                </td>
                <td className="py-1 px-1 text-right tabular-nums w-14 sm:w-16 min-w-14">
                  {calculateTeamStatistics(getSortedTeams(localTeams, teamSortType), employees, teamActualSales, teamIncentiveCalculations, budgetData, selectedMonth).totalSalary.toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums font-bold">
                  {(() => {
                    // 가중평균 인건비율 계산: 총인건비 ÷ 총순수목표매출 × 100
                    const sortedTeams = getSortedTeams(localTeams, teamSortType);
                    const totalSalary = calculateTeamStatistics(sortedTeams, employees, teamActualSales, teamIncentiveCalculations, budgetData, selectedMonth).totalSalary;
                    const totalPureTargetSales = sortedTeams.reduce((total, teamName) => {
                      const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName, employees);
                      const teamPureTarget = calculatePureTargetSales(teamSalaryTotal, getEffectivePersonnelPercentage(teamName));
                      return total + teamPureTarget;
                    }, 0);
                    const weightedAveragePercentage = totalPureTargetSales > 0 ? (totalSalary / totalPureTargetSales) * 100 : 0;
                    return `${weightedAveragePercentage.toFixed(1)}%`;
                  })()}
                </td>
                {/* 순수 목표매출 합계 */}
                <td className="py-1 px-1 text-right tabular-nums font-bold">
                  {(() => {
                    const sortedTeams = getSortedTeams(localTeams, teamSortType);
                    const hasAnyMaskedData = sortedTeams.some(teamName => shouldMaskTeamData(teamName));
                    
                    if (hasAnyMaskedData) {
                      return <span className="font-semibold text-gray-500">****</span>;
                    }
                    
                    // 🔧 실시간 계산으로 변경 - 개별 순수목표매출과 동일한 로직 사용
                    const totalPureTargetSales = sortedTeams.reduce((total, teamName) => {
                      const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName, employees);
                      const teamPureTarget = calculatePureTargetSales(teamSalaryTotal, getEffectivePersonnelPercentage(teamName));
                      return total + teamPureTarget;
                    }, 0);
                    return totalPureTargetSales.toLocaleString();
                  })()}
                </td>
                {/* 최종 목표매출 합계 */}
                <td className="py-1 px-1 text-right tabular-nums font-bold">
                  {(() => {
                    const sortedTeams = getSortedTeams(localTeams, teamSortType);
                    const hasAnyMaskedData = sortedTeams.some(teamName => shouldMaskTeamData(teamName));
                    
                    if (hasAnyMaskedData) {
                      return <span className="font-semibold text-gray-500">****</span>;
                    }
                    
                    // 🔧 실시간 계산으로 변경 - 순수목표매출 + 지난달 미달성 조정
                    const realTimeTotalTargetSales = sortedTeams.reduce((total, teamName) => {
                      const teamEmployees = employees.filter(emp => emp.team === teamName);
                      const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName, employees);
                      const teamPureTarget = calculatePureTargetSales(teamSalaryTotal, getEffectivePersonnelPercentage(teamName));
                      
                      // 지난달 미달성 조정 적용
                      const teamLeader = teamEmployees.find(emp => emp.role === '팀장');
                      const currentLeaderName = teamLeader?.name || '';
                      const canApplyAdjustment = applyPreviousMonthAdjustment && 
                                               previousMonthData && 
                                               checkTeamAndLeaderMatch(teamName, currentLeaderName, previousMonthData);
                      const previousUnderperformance = canApplyAdjustment ? calculatePreviousMonthUnderperformance(teamName, previousMonthData) : 0;

                      return total + resolveFinalTargetSales(budgetData, selectedMonth, teamName, teamPureTarget);
                    }, 0);
                    return realTimeTotalTargetSales.toLocaleString();
                  })()}
                </td>
                {/* 실제매출 합계 */}
                <td className="py-1 px-1 text-right tabular-nums font-bold">
                  {calculateTeamStatistics(getSortedTeams(localTeams, teamSortType), employees, teamActualSales, teamIncentiveCalculations, budgetData, selectedMonth).totalActualSales.toLocaleString()}
                </td>
                {/* 초과/미달 합계 */}
                <td className="py-1 px-1 text-right tabular-nums font-bold">
                  {(() => {
                    const sortedTeams = getSortedTeams(localTeams, teamSortType);
                    const hasAnyMaskedData = sortedTeams.some(teamName => shouldMaskTeamData(teamName));
                    
                    if (hasAnyMaskedData) {
                      return <span className="font-semibold text-gray-500">****</span>;
                    }
                    
                    const stats = calculateTeamStatistics(sortedTeams, employees, teamActualSales, teamIncentiveCalculations, budgetData, selectedMonth);
                    
                    // 🔧 실시간 계산된 최종목표매출을 기준으로 초과/미달 계산
                    const realTimeTotalTargetSales = sortedTeams.reduce((total, teamName) => {
                      const teamEmployees = employees.filter(emp => emp.team === teamName);
                      const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName, employees);
                      const teamPureTarget = calculatePureTargetSales(teamSalaryTotal, getEffectivePersonnelPercentage(teamName));
                      
                      // 지난달 미달성 조정 적용
                      const teamLeader = teamEmployees.find(emp => emp.role === '팀장');
                      const currentLeaderName = teamLeader?.name || '';
                      const canApplyAdjustment = applyPreviousMonthAdjustment && 
                                               previousMonthData && 
                                               checkTeamAndLeaderMatch(teamName, currentLeaderName, previousMonthData);
                      const previousUnderperformance = canApplyAdjustment ? calculatePreviousMonthUnderperformance(teamName, previousMonthData) : 0;

                      return total + resolveFinalTargetSales(budgetData, selectedMonth, teamName, teamPureTarget);
                    }, 0);
                    
                    const totalExceededSales = stats.totalActualSales - realTimeTotalTargetSales;
                    return (
                      <span className={`${totalExceededSales > 0 ? 'text-green-600' : totalExceededSales < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {totalExceededSales.toLocaleString()}
                      </span>
                    );
                  })()}
                </td>
                {/* 차감분 합계 */}
                <td className="py-1 px-1 text-right tabular-nums font-bold text-red-600">
                  {calculateTeamStatistics(getSortedTeams(localTeams, teamSortType), employees, teamActualSales, teamIncentiveCalculations, budgetData, selectedMonth).totalDeductionAmount.toLocaleString()}
                </td>
                {/* 인센티브산출매출 합계 */}
                <td className="py-1 px-1 text-right tabular-nums font-bold text-purple-600">
                  {calculateTeamStatistics(getSortedTeams(localTeams, teamSortType), employees, teamActualSales, teamIncentiveCalculations, budgetData, selectedMonth).totalIncentiveCalculationSales.toLocaleString()}
                </td>
                {/* 인센티브비율(%) */}
                <td className="py-1 px-1 text-center w-20 sm:w-24 min-w-20">-</td>
                {/* 팀장/팀원배분 */}
                <td className="py-1 px-1 text-center w-40 min-w-40">-</td>
                {/* 지난달조정 */}
                <td className="py-1 px-1 text-center w-32 min-w-32">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
